import { readAlert } from '@/api/cs';
import { ALERT_RENDER_MAP } from '@/constants/cs';
import { useAppDispatch } from '@/hooks/useReduxHooks';
import { updateCs } from '@/store/modules/cs';
import { Notification } from '@/types/cs.type';
import { comma } from '@/util/format';
import { format, subDays } from 'date-fns';
import { useMemo } from 'react';
import { useMutation } from 'react-query';
import styled, { css } from 'styled-components';

type Props = {
  notification: Notification;
  onOpenBottomSheet: (info: any) => void;
};

function CsAlertItem({ notification, onOpenBottomSheet }: Props) {
  const dispatch = useAppDispatch();

  const { mutate: mutateReadAlert } = useMutation(readAlert);

  const onOpenChatModal = (chatId: number) => {
    dispatch(
      updateCs({
        isOpenChat: true,
        chatId: chatId || null,
      }),
    );
  };

  const onReadAlert = ({
    chatId,
    notificationType,
  }: {
    chatId: number;
    notificationType: 'ONE_ON_ONE' | 'REQUIREMENT_RESULT' | 'ADMIN_NOTICE';
  }) => {
    mutateReadAlert(
      { chatId, notificationType },
      {
        onSuccess: () => {
          if (
            notificationType === 'ONE_ON_ONE' ||
            notificationType === 'ADMIN_NOTICE'
          ) {
            onOpenChatModal(chatId);
          } else {
            onOpenBottomSheet(notification.message);
          }
        },
      },
    );
  };

  const getTime = (dateTime: string) => {
    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(new Date(), 'yyyy-MM-dd')
    ) {
      return format(new Date(dateTime), 'HH:mm');
    }

    if (
      format(new Date(dateTime), 'yyyy-MM-dd') ===
      format(subDays(new Date(), 1), 'yyyy-MM-dd')
    ) {
      return '어제';
    }

    return format(new Date(dateTime), 'M월 d일');
  };

  const createdTime = useMemo(() => {
    if (notification.message.createdAt) {
      if (
        format(new Date(notification.message.createdAt), 'yyyy-MM-dd') ===
        format(new Date(), 'yyyy-MM-dd')
      ) {
        return format(new Date(notification.message.createdAt), '오늘 HH:mm');
      }

      return format(
        new Date(notification.message.createdAt),
        'yyyy-MM-dd HH:mm',
      );
    }
    return null;
  }, [notification]);

  const requestInfo = useMemo(() => {
    if (notification.message.notificationType === 'REQUIREMENT_RESULT') {
      return JSON.parse(notification.message.message);
    }
    return null;
  }, [notification]);

  const isRejectedRefund =
    notification.message.notificationType === 'REQUIREMENT_RESULT' &&
    JSON.parse(notification.message.message).type === 'REFUND_CREATE_REJECTED';

  return (
    <AlertItem
      key={notification.id}
      onClick={() =>
        onReadAlert({
          chatId: notification.message.chatId,
          notificationType: notification.message.notificationType as
            | 'ONE_ON_ONE'
            | 'REQUIREMENT_RESULT',
        })
      }
    >
      <AlertHeader read={notification.read}>
        <strong>
          {notification.message.notificationType === 'REQUIREMENT_RESULT'
            ? '처리 내역 알림'
            : notification.message.notificationType === 'ADMIN_NOTICE'
            ? 'KTP 고객지원팀'
            : '1:1 문의 알림'}
        </strong>
        <span>{getTime(notification.createdAt)}</span>
      </AlertHeader>

      {notification.message.notificationType === 'ONE_ON_ONE' && (
        <ChatTitle read={notification.read}>
          <span>대화 시작</span>
          <strong>{createdTime}</strong>
        </ChatTitle>
      )}

      {notification.message.notificationType === 'REQUIREMENT_RESULT' && (
        <RequestInfo read={notification.read}>
          {requestInfo?.type.includes('REFUND') ? (
            <RequestInfoList>
              <RequestInfoItem>
                <RequestInfoTitle>고객명</RequestInfoTitle>
                <RequestInfoData>
                  {JSON.parse(notification.message.message).touristName}
                </RequestInfoData>
              </RequestInfoItem>
              <RequestInfoItem>
                <RequestInfoTitle>시술금액</RequestInfoTitle>
                <RequestInfoData>
                  {comma(
                    JSON.parse(notification.message.message).productAmount,
                  )}
                  원
                </RequestInfoData>
              </RequestInfoItem>
            </RequestInfoList>
          ) : requestInfo?.type === 'PACKAGE_ACCEPTED' ? (
            <>
              {requestInfo?.packageNames.map((packageName: string) => (
                <RequestInfoList isPackage>
                  <RequestInfoTitle>{packageName}</RequestInfoTitle>
                </RequestInfoList>
              ))}
              {requestInfo?.etc && (
                <RequestInfoList isPackage>
                  <RequestInfoTitle>기타</RequestInfoTitle>
                  <RequestInfoData>{requestInfo.etc}</RequestInfoData>
                </RequestInfoList>
              )}
            </>
          ) : null}
        </RequestInfo>
      )}

      <AlertResult isRed={isRejectedRefund} read={notification.read}>
        {notification.message.notificationType === 'ONE_ON_ONE'
          ? ALERT_RENDER_MAP['ONE_ON_ONE']
          : notification.message.notificationType === 'ADMIN_NOTICE'
          ? ALERT_RENDER_MAP['ADMIN_NOTICE']
          : ALERT_RENDER_MAP[JSON.parse(notification.message.message).type]}
      </AlertResult>
    </AlertItem>
  );
}

const AlertHeader = styled.div<{ read: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 4px;

  strong {
    color: ${({ read }) => (read ? '#B0B1B2' : '#3a3b3e')};
    font-size: 16px;
    font-weight: 600;
    line-height: 150%;
  }

  span {
    color: ${({ read }) => (read ? '#B0B1B2' : '#5f6165')};
    font-size: 14px;
    line-height: 150%;
  }
`;

const ChatTitle = styled.div<{ read: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  ${({ read }) =>
    read &&
    css`
      opacity: 0.4;
    `}

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f4f8ff;
    border-radius: 2px;
    font-weight: 500;
    font-size: 12px;
    line-height: 150%;
    letter-spacing: -3%;
    color: #5089f8;
  }

  strong {
    font-weight: 600;
    font-size: 16px;
    line-height: 150%;
    letter-spacing: -3%;
  }
`;

const AlertResult = styled.p<{ isRed: boolean; read: boolean }>`
  text-align: left;
  color: ${({ isRed, read }) =>
    isRed
      ? read
        ? 'rgba(255, 108, 136,0.4)'
        : '#FF2D55'
      : read
      ? '#BFC0C1'
      : '#3a3b3e'};
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`;

const RequestInfoList = styled.dl<{ isPackage?: boolean }>`
  color: #80848a;
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  letter-spacing: -2%;
  background-color: #f5f6f7;
  width: 100%;
  height: 24px;
  border-radius: 6px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 4px;

  ${({ isPackage }) =>
    isPackage &&
    css`
      display: inline-flex;
      width: auto;
      flex-shrink: 0;
    `}
`;

const RequestInfoItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;

  &:first-child {
    position: relative;
    &:after {
      content: '';
      width: 1px;
      height: 12px;
      border-radius: 100px;
      background-color: #cbccce;
      position: absolute;
      top: 50%;
      right: -8px;
      transform: translateY(-50%);
    }
  }
`;

const RequestInfoTitle = styled.dt`
  font-weight: 700;
  flex-shrink: 0;
`;

const RequestInfoData = styled.dd`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  text-align: left;
`;

const AlertItem = styled.button`
  width: 100%;
  padding: 14px 0%;

  &:not(:last-child) {
    border-bottom: 1px solid #e5e6e8;
  }
`;

const RequestInfo = styled.div<{ read: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  width: 278px;
  overflow: hidden;
  ${({ read }) =>
    read &&
    css`
      opacity: 0.4;
    `}
`;

export default CsAlertItem;
